/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import 'normalize.css';

import './semantic_2.4.1.min.css';

import './App.css';
import './ThirdParty.css';

import {
  useDisclosure,
  CircularProgress,
  MarketingModal,
} from '@ouster/webviz-react-components';

import {
  ThemeProvider as MaterialThemeProvider,
  createTheme,
} from '@mui/material/styles';
import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from '@tanstack/react-query';
import { Popover } from '@typeform/embed-react';
import React, { useEffect, useState, useMemo } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  Navigate,
  useLocation,
  useSearchParams,
  useNavigate,
  useParams,
} from 'react-router-dom';
import styled from 'styled-components/macro';

import ConfirmDialog from './components/ConfirmDialog';
import ModalContainer from './components/ModalContainer';
import ScreenRecommendation from './components/users/ScreenRecommendation';
import ArchitectSceneList from './containers/ArchitectSceneList';
import { ArchitectSceneViewer } from './containers/ArchitectSceneViewer';
import { DriveViewer } from './containers/DriveViewer';
import { FlexColumnWithNav } from './containers/NavBar';
import { UploadView } from './containers/UploadView';
import useChakraHook from './hooks/useChakraHook';
import useWindowResize from './hooks/useWindowResize';
import { KeycloakProvider, useKeycloak } from './KeycloakContext';
import AdminDashboardLayout from './layouts/AdminDashboardLayout';
import AuthLayout from './layouts/AuthLayout';
import DefaultLayout from './layouts/DefaultLayout';
import PublicLayout from './layouts/PublicLayout';
import AdminDashboardOrgDetail from './pages/adminDashboard/AdminDashboardOrgDetail';
import OrganizationLookUp from './pages/adminDashboard/OrganizationLookUp';
import RecordingLookUp from './pages/adminDashboard/RecordingLookUp';
import RecordingsManagement from './pages/adminDashboard/RecordingsManagement';
import UserLookUp from './pages/adminDashboard/UserLookUp';
import AuthRedirect from './pages/auth/AuthRedirect';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import LandingPage from './pages/auth/LandingPage';
import OsdAnnouncement from './pages/auth/OsdAnnouncement';
import PostRegistrationPage from './pages/auth/PostRegistrationPage';
import RegisterPage from './pages/auth/RegisterPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import SignUpPage from './pages/auth/SignUpPage';
import OrganizationManagement from './pages/OrganizationManagement';
import PreventIframe from './pages/PreventIframe';
import DriveListPage from './pages/privateList/DriveListPage';
import PrivatePartnerPreview from './pages/privateList/PrivatePartnerPreview';
import PrivatePublicCollection from './pages/privateList/PrivatePublicCollection';
import PrivateRecordings from './pages/privateList/PrivateRecordings';
import SharedWithOusterPage from './pages/privateList/SharedWithOusterPage';
import PublicCollection from './pages/public/PublicCollection';
import PublicPartnerPreview from './pages/public/PublicPartnerPreview';
import PublicRecordings from './pages/public/PublicRecordings';
import PublicSharedWithOuster from './pages/public/PublicSharedWithOuster';
import { useStoreState, useStoreActions } from './reducers';
import { DataCategory } from './types';
import { MINIMAL_WIDTH } from './utils/constants';
import { registerSearchParamsSetter } from './utils/searchParams';

const FullScreenFlexContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  height: 100vh;
  align-items: stretch;
  background-color: var(--color-unify-darker-blue);
`;

const StatusContainer = styled.div`
  /* Keep the text to a maximum of 40ch for readibility*/
  max-width: 40ch;
  /* Center on the page */
  margin: auto;
  display: grid;
  row-gap: 3rem;
  justify-items: center;
  font-size: 1.75rem;
  font-weight: 500;
  line-height: normal;
`;

const AppLayout = () => (
  <FullScreenFlexContainer className="App">
    <Outlet />
  </FullScreenFlexContainer>
);

/* 
  After publication, authenticated doesn't mean the user can access the app
  user must existed in the db as well
  add check in private outlet
*/

const PrivateOutlet = () => {
  // Removed debugging log statement
  const { isAuthenticated, user, appUser, curUserOrg } = useStoreState(
    (storeState) => ({
      ...storeState.user,
    })
  );
  const { fetchUserRoleByIdentity, fetchUserEulaStatus, fetchUserOrgs } =
    useStoreActions((actions) => actions.user);
  const { loadOrgFilterOptions } = useStoreActions(
    (actions) => actions.driveList
  );
  const { loadNewContentStatus } = useStoreActions((actions) => actions.drive);

  const { keycloak, refreshKeycloakToken } = useKeycloak();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOpen: uploadVisibility } = useStoreState(
    (storeState) => storeState.recordingUpload
  );

  const { orgSpaceUsage } = useStoreState(
    (storeState) => storeState.organization
  );

  const { fetchOrgSpaceUsage } = useStoreActions(
    (actions) => actions.organization
  );
  const { setIsOpen: setUploadVisibility } = useStoreActions(
    (actions) => actions.recordingUpload
  );
  const { showToast } = useChakraHook();
  const queryClient = useQueryClient();

  useEffect(() => {
    const getOrgSpaceUsage = async () => {
      if (!curUserOrg?.id) return;
      try {
        await fetchOrgSpaceUsage(curUserOrg.id);
      } catch (error) {
        console.error('fetchOrgSpaceUsage after upload', error);
      }
    };
    /* 
      We need to fetch the usage when org id change or
      when user open the upload window. so we can make sure whenever user tries to upload
      we always have its latest usage.
    */
    getOrgSpaceUsage();
  }, [curUserOrg?.id, fetchOrgSpaceUsage, uploadVisibility]);

  useEffect(() => {
    /* 
      Make sure we refetch the shared list when org change.
    */
    queryClient.refetchQueries({ queryKey: ['shared-with-list'], type: 'all' });
  }, [curUserOrg?.id, queryClient]);

  const refreshToast = (dataCategory: DataCategory, isAutoSubmit: boolean) => {
    /* 
      We only need to do following when we are on the recording page
    */
    if (location.pathname === '/drive/private') {
      const categories = searchParams.getAll('data_category');
      // when the filter has category but not included the category that newly uploaded
      // belongs to, we need to update the url to include it
      if (categories.length > 0 && !categories.includes(dataCategory.name)) {
        const newParams = new URLSearchParams(searchParams);
        newParams.append('data_category', dataCategory.name);
        setSearchParams(newParams);
        navigate({ search: newParams.toString() });
      }
      if (curUserOrg?.id) loadOrgFilterOptions(curUserOrg.id);

      // when we are on the recording page, we need to force query to refetch
      queryClient.refetchQueries({ queryKey: ['drive-list'], type: 'all' });
    } else {
      // when we are not on the recording page, we just invalidate the query
      // so when it lands on the page, the query will do the refetch
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      queryClient.invalidateQueries({ queryKey: ['drive-list'] });
    }

    if (isAutoSubmit) {
      showToast({
        title: 'Submission Confirmed!',
        description: 'Your recording has been successfully submitted.',
        status: 'success',
        isStay: true,
        closeOnOutsideClick: true,
      });
    }
  };

  const [loading, setLoading] = useState(true); // Introduce a loading state
  const { width: windowWidth } = useWindowResize();

  useEffect(() => {
    if (user?.identity_id) {
      if (sessionStorage.getItem('appAuthId')) {
        navigate(
          `/app_auth?app_auth_id=${sessionStorage.getItem('appAuthId')}`
        );
        return;
      }
      if (sessionStorage.getItem('architectId')) {
        const url = new URL(window.location.origin);
        url.pathname = `/architect/${sessionStorage.getItem('architectId')}`;
        window.location.href = url.toString();

        sessionStorage.removeItem('architectId');
        return;
      }

      const fetchAppUser = async () => {
        try {
          await fetchUserRoleByIdentity(user.identity_id);
          await fetchUserOrgs(user.identity_id);

          /* 
            we don't need to await here and slow user login.
            patch user the eula status can be done in background since it won't affect anything.
          */
          try {
            fetchUserEulaStatus(user.identity_id);
          } catch (error) {
            console.error('Fetching user eula status error,', error);
          }
        } catch (error) {
          // no need to show error to user
          // it just mean the user isn't existed in db
          console.error('Fetching user by id,', error);
        } finally {
          setLoading(false);
        }
      };

      fetchAppUser();
    }
  }, [
    user?.identity_id,
    fetchUserRoleByIdentity,
    fetchUserEulaStatus,
    fetchUserOrgs,
    navigate,
  ]);

  useEffect(() => {
    if (curUserOrg?.id) {
      loadNewContentStatus({ organizationId: curUserOrg.id });
    }
  }, [curUserOrg?.id, loadNewContentStatus]);

  useEffect(() => {
    // Handle navigation logic
    if (
      (keycloak && keycloak.authenticated === false) ||
      (isAuthenticated && appUser === undefined && !loading)
    ) {
      // Store the original path before redirection
      sessionStorage.setItem('originalPath', window.location.pathname);
      navigate('/', { replace: true });
    }
  }, [keycloak, isAuthenticated, appUser, navigate, loading]);

  useEffect(() => {
    const handleWindowFocus = () => {
      if (keycloak?.isTokenExpired()) {
        refreshKeycloakToken();
      }
    };

    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [keycloak, refreshKeycloakToken]);

  /* 
    checking screen size. when the screen is too small
    show the warning modal per session.
    User can choose to dismiss it if they don't mind and it will not show up
    again in the same session.
  */
  useEffect(() => {
    if (
      !sessionStorage.getItem('showSmallWindowWarnModal') &&
      windowWidth < MINIMAL_WIDTH
    ) {
      onOpen();
    } else if (isOpen) onClose();
  }, [isOpen, onClose, onOpen, windowWidth]);

  if (loading) {
    return (
      <StatusContainer>
        <span>Authenticating...</span>
        <CircularProgress isIndeterminate size="50px" thickness={10} />
      </StatusContainer>
    );
  }

  if (isAuthenticated && appUser !== undefined) {
    return (
      <>
        {uploadVisibility && curUserOrg && (
          <UploadView
            user={user}
            closeUploadViewFn={() => setUploadVisibility(false)}
            refreshToastFn={refreshToast}
            orgId={curUserOrg.id}
            orgName={curUserOrg.name}
            orgSpaceUsage={orgSpaceUsage}
          />
        )}
        <Outlet />
        <ModalContainer
          isOpen={isOpen}
          onClose={() => {
            sessionStorage.setItem('showSmallWindowWarnModal', 'true');
            onClose();
          }}
          header="Optimize Your Experience"
          footer={null}
          content={<ScreenRecommendation />}
        />
      </>
    );
  }

  return (
    <FlexColumnWithNav>
      <CircularProgress isIndeterminate size="50px" thickness={10} />
    </FlexColumnWithNav>
  );
};

// TODO(pierre) on 2021-12-20: The DriveList component, unlike
// DriveViewer, does not come with a Navbar. This isn't super clean and
// consistent. We should probably instead let every page handle their
// own navbar and pass the necessary info (i.e. the nav tabs and whether
// authenticated or not) through easy peasy or a React context

const ArchitectSceneListPage = () => {
  const { user } = useStoreState((storeState) => ({
    ...storeState.user,
  }));

  return <ArchitectSceneList user={user} />;
};

const RootRedirect = () => {
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);

  // Get a specific parameter value
  const specificParam = queryParams.get('path');

  let redirectRoute;
  if (specificParam) {
    redirectRoute = specificParam;
  } else {
    // The new root of the app should be the landing page.
    return <LandingPage />;
  }
  return <Navigate replace to={redirectRoute} />;
};

const KeycloakLoginRedirect = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const appAuthId: string | null = params.get('app_auth_id');
  if (appAuthId) {
    sessionStorage.setItem('appAuthId', appAuthId);
  }
  const architectId: string | null = params.get('architect');
  if (architectId) {
    sessionStorage.setItem('architectId', architectId);
  }
  const appAuthProtocol: string | null = params.get('app_auth_protocol');
  if (appAuthProtocol) {
    sessionStorage.setItem('appAuthProtocol', appAuthProtocol);
  }
  const organizationId = params.get('organization_id');
  if (organizationId) {
    localStorage.setItem('lastOrgId', organizationId);
  }

  const { keycloak } = useKeycloak();

  useEffect(() => {
    if (keycloak && !keycloak.authenticated) {
      keycloak.login({
        redirectUri: `${window.location.origin}`,
      });
    }
  }, [keycloak]);

  // this is first time introducing osd version check. Hence, we will
  // only do the check when osd_version is provided. in future, we will might do detailed
  // version check and provide version specific message.
  // make sure we only do this for osd auth request, so osw won't be affected.
  // comment out the osd version check since we will still support two keycloak domain in this release.
  // TODO: (vivian) remove the comment below when we decide to enforce osd version check
  // const osdVersion: string | null = params.get('osd_version');
  // if (!osdVersion && appAuthId) {
  //   return <Navigate to="/osd_announcement" replace />;
  // }
  // if (osdVersion) sessionStorage.setItem('osdVersion', osdVersion);

  if (keycloak?.authenticated) {
    return <Navigate to="/" replace />;
  }

  return null;
};

/* 
  To prevent user accidentally remove the orgId from the url, we moved the private drive router from drive/:driverId?orgId=orgId to organization/:orgId/drive/:driveId
  This component will redirect the old route to the new one.

  when the orgId is not provided in the url, we will use the current user org id, this is the same behavior as we handle in the DriveViewer component.
*/
const RedirectOldDriveRoute = () => {
  const { curUserOrg } = useStoreState((storeState) => ({
    ...storeState.user,
  }));

  const [searchParams] = useSearchParams();
  const { driveId } = useParams(); // Extract driveId from the route params
  const navigate = useNavigate();

  useEffect(() => {
    const orgId = searchParams.get('orgId') ?? curUserOrg?.id.toString();
    const frame = searchParams.get('frame');

    if (orgId && driveId) {
      const newSearchParams = new URLSearchParams();
      newSearchParams.set('orgId', orgId);
      if (frame) {
        newSearchParams.set('frame', frame);
      }
      navigate(
        `/organization/${orgId}/drive/${driveId}?${newSearchParams.toString()}`,
        { replace: true }
      );
    }
  }, [searchParams, driveId, navigate, curUserOrg?.id]);

  return null;
};

const AppRoutes = () => {
  const [, setSearchParams] = useSearchParams();
  registerSearchParamsSetter(setSearchParams);

  return (
    <Routes>
      <Route path="/" element={<AppLayout />}>
        <Route element={<AuthLayout />}>
          <Route index element={<RootRedirect />} />
          <Route path="sign_up" element={<SignUpPage />} />
          <Route path="login" element={<KeycloakLoginRedirect />} />

          <Route path="forgot_password" element={<ForgotPasswordPage />} />
          <Route path="reset_password" element={<ResetPasswordPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="post-registration" element={<PostRegistrationPage />} />
        </Route>

        <Route path="app_auth" element={<AuthRedirect />} />
        <Route path="osd_announcement" element={<OsdAnnouncement />} />

        {/* public in-app pages for non-logged user to experience */}
        <Route path="/public" element={<PublicLayout />}>
          {/* If user visit the /public directly, redirect user to the sample page */}
          <Route
            index
            element={<Navigate to="sample_library/public_collection" replace />}
          />
          <Route path="recordings" element={<PublicRecordings />} />
          <Route
            path="shared_with_ouster"
            element={<PublicSharedWithOuster />}
          />
          {/* Sample Library routes */}
          <Route path="sample_library">
            {/* Index route redirects to first submenu */}
            <Route
              index
              element={<Navigate to="public_collection" replace />}
            />
            <Route path="public_collection" element={<PublicCollection />} />
            <Route path="partner_preview" element={<PublicPartnerPreview />} />
          </Route>
        </Route>

        <Route element={<PrivateOutlet />}>
          <Route
            path="architect_scene/:architectSceneId"
            element={<ArchitectSceneViewer />}
          />
          <Route path="drive/:driveId" element={<RedirectOldDriveRoute />} />
          <Route
            path="organization/:orgId/drive/:driveId"
            element={<DriveViewer />}
          />

          {/* Routes with DefaultLayout */}
          <Route element={<DefaultLayout />}>
            <Route
              index
              path="architect_scene"
              element={<ArchitectSceneListPage />}
            />
            <Route path="drive/">
              <Route index element={<DriveListPage />} />
              <Route path="private" element={<PrivateRecordings />} />

              <Route path="sample_library">
                {/* When user clicks sample_library, redirect to public_collection */}
                <Route
                  index
                  element={<Navigate to="public_collection" replace />}
                />
                <Route
                  path="public_collection"
                  element={<PrivatePublicCollection />}
                />
                <Route
                  path="partner_preview"
                  element={<PrivatePartnerPreview />}
                />
              </Route>
            </Route>

            <Route
              index
              path="organization"
              element={<OrganizationManagement />}
            />
            <Route path="admin_dashboard" element={<AdminDashboardLayout />}>
              <Route index element={<Navigate to="user" replace />} />
              <Route path="user" element={<UserLookUp />} />
              <Route path="recording" element={<RecordingLookUp />} />
              <Route
                path="recordings_management"
                element={<RecordingsManagement />}
              />
              <Route path="org" element={<OrganizationLookUp />} />
              <Route path="org/:orgId" element={<AdminDashboardOrgDetail />} />
            </Route>
            <Route
              index
              path="shared_with_ouster"
              element={<SharedWithOusterPage />}
            />
          </Route>
        </Route>

        <Route path="share">
          <Route path=":publishedDriveUuid" element={<DriveViewer />} />
        </Route>

        <Route path="architect/share">
          <Route
            path=":publishedArchitectSceneUuid"
            element={<ArchitectSceneViewer />}
          />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
};

const queryClient = new QueryClient();

const App: React.FC = () => {
  const materialDarkTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: 'dark',
          primary: {
            // mui didn't support variable here, hence, color code is passed here
            main: '#5e79fb',
          },
        },
        components: {
          MuiChip: {
            styleOverrides: {
              colorSuccess: {
                backgroundColor: '#18653c', // custom green
                color: '#fff',
              },
              colorError: {
                backgroundColor: '#E53E3E', // custom red
                color: '#fff',
              },
              colorInfo: {
                backgroundColor: '#3182CE', // custom blue
                color: '#fff',
              },
              colorWarning: {
                backgroundColor: '#f6e865', // custom orange
                color: '#black',
              },
            },
          },
        },
      }),
    []
  );

  /* 
    This is to detect logout in another tab
    When user logout in another tab, we want to detect it and refresh the current tab
    keycloak logout is a global logout, it kills all the session in all tabs however, visually, it doesn't appear like that until user refresh and do any action. Hence, we trigger a refresh when logout is detected in another tab.
  */
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'logout-event') {
        window.location.href = '/login';
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  /* 
    When the app detect its running inside an iframe, it will render this component and inform the user
  */
  const [isInIframe, setIsInIframe] = useState(false);
  useEffect(() => {
    if (window.self !== window.top) {
      setIsInIframe(true);
    }
  }, []);

  const { appUser } = useStoreState((storeState) => storeState.user);
  const { updateUserMarketingPreference } = useStoreActions(
    (actions) => actions.user
  );
  const [isMarketingModalOpen, setIsMarketingModalOpen] = useState(false);

  useEffect(() => {
    if (appUser && appUser.email_opt_out === null) {
      setIsMarketingModalOpen(true);
    }
  }, [appUser]);

  const handleMarketingSave = async (optIn: boolean) => {
    try {
      if (appUser && appUser.identity_id) {
        await updateUserMarketingPreference({
          identity_id: appUser.identity_id,
          opt_out: !optIn,
        });
        setIsMarketingModalOpen(false);
      }
    } catch (error) {
      console.error('Failed to update marketing preferences:', error);
    }
  };

  if (isInIframe) return <PreventIframe />;

  return (
    <BrowserRouter>
      <KeycloakProvider>
        <MaterialThemeProvider theme={materialDarkTheme}>
          <QueryClientProvider client={queryClient}>
            <AppRoutes />
            <ConfirmDialog />
            <MarketingModal
              isOpen={isMarketingModalOpen}
              handleClose={() => setIsMarketingModalOpen(false)}
              handleSave={handleMarketingSave}
            />
            <Popover
              id="XfXOrUKH"
              hidden={{
                name: appUser?.name ?? '',
                email: appUser?.email ?? '',
                from: window.location.pathname,
                platform: 'OSW',
              }}
              height={600}
              buttonColor="#5e79fb"
            />
          </QueryClientProvider>
        </MaterialThemeProvider>
      </KeycloakProvider>
    </BrowserRouter>
  );
};

export default App;
